import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { abortWorkOrder } from '#/api/momMgmt/workOrder/abort';
import { approveWorkOrder } from '#/api/momMgmt/workOrder/approve';
import { closeWorkOrder } from '#/api/momMgmt/workOrder/close';
import { deleteWorkOrder } from '#/api/momMgmt/workOrder/delete';
import { fetchWorkOrderDetail } from '#/api/momMgmt/workOrder/fetchDetail';
import { rejectWorkOrder } from '#/api/momMgmt/workOrder/reject';
import { reopenWorkOrder } from '#/api/momMgmt/workOrder/reopen';
import { submitWorkOrder } from '#/api/momMgmt/workOrder/submit';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  AppDialog,
  useConfirmDialog,
} from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import { displayOrDash } from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import DocumentRejectReasonDialog
  from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS }
  from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';
import {
  getWorkOrderApiErrorMessage,
  isWorkOrderVersionConflict,
} from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  canAbortWorkOrder,
  canApproveWorkOrder,
  canCloseWorkOrder,
  canDeleteWorkOrderDraft,
  canRejectWorkOrder,
  canReopenWorkOrder,
  canSubmitWorkOrder,
} from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { toast } from '#/utils/toast';
import {
  customerOrderDetailQueryKey,
  workOrderDetailQueryKey,
  workOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';

function pickUpdatedAt(detailLike) {
  return detailLike?.record?.updatedAt ?? detailLike?.updatedAt ?? null;
}

function SummaryCell({ label, value, className, children }) {
  return (
    <div className={className}>
      <p className="text-xs text-gray-500">{label}</p>
      {children ?? (
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {value == null || value === '' ? '—' : value}
        </p>
      )}
    </div>
  );
}

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  children: PropTypes.node,
};

/**
 * 客訂詳情工單 Tab「預覽」：摘要＋狀態流轉（對齊 RelatedDoc／CommandBar mutation，不嵌 CommandBar）。
 * 開啟時單次 GET detail（updatedAt／權威 status）；製造標的／數量吃列表列。
 */
const CustomerOrderWorkOrderPreviewDialog = ({
  open,
  onOpenChange,
  workOrder,
  customerOrderId = null,
}) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectOpen, setRejectOpen] = useState(false);
  const workOrderId = workOrder?.workOrderId ?? '';

  const detailQuery = useQuery({
    queryKey: workOrderDetailQueryKey(workOrderId),
    queryFn: () => fetchWorkOrderDetail(workOrderId),
    enabled: open && Boolean(workOrderId),
  });

  const detail = detailQuery.data?.data;
  const status = detail?.status || workOrder?.status || '';
  const workOrderNo = displayOrDash(
    detail?.workOrderNo || workOrder?.workOrderNo,
  );
  const manufacturingTarget = [
    workOrder?.mtrNo,
    workOrder?.mtrName,
  ].filter(Boolean).join(' ') || '—';
  const qtyLabel = (
    workOrder?.qty == null || workOrder?.qty === ''
      ? '—'
      : String(workOrder.qty)
  );
  const detailPath = workOrderId ? ROUTES.workOrderDetail(workOrderId) : null;

  const canSubmit = canSubmitWorkOrder(status);
  const canApprove = canApproveWorkOrder(status);
  const canReject = canRejectWorkOrder(status);
  const canClose = canCloseWorkOrder(status);
  const canAbort = canAbortWorkOrder(status);
  const canReopen = canReopenWorkOrder(status);
  const canDeleteDraft = canDeleteWorkOrderDraft(status);

  const mutation = useMutation({
    mutationFn: async ({ action, rejectReason }) => {
      if (!workOrderId) {
        throw new Error('缺少工單 id');
      }
      if (action === 'delete') {
        return deleteWorkOrder(workOrderId);
      }
      const updatedAt = pickUpdatedAt(detail);
      if (!updatedAt) {
        throw new Error('缺少更新時間，請重新載入後再試');
      }
      if (action === 'submit') {
        return submitWorkOrder(workOrderId, { updatedAt });
      }
      if (action === 'approve') {
        return approveWorkOrder(workOrderId, { updatedAt });
      }
      if (action === 'reject') {
        return rejectWorkOrder(workOrderId, { updatedAt, rejectReason });
      }
      if (action === 'close') {
        return closeWorkOrder(workOrderId, { updatedAt });
      }
      if (action === 'abort') {
        return abortWorkOrder(workOrderId, { updatedAt });
      }
      if (action === 'reopen') {
        return reopenWorkOrder(workOrderId, { updatedAt });
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectOpen(false);
      }
      await queryClient.invalidateQueries({
        queryKey: workOrderListQueryKeyPrefix(),
      });
      await queryClient.invalidateQueries({
        queryKey: workOrderDetailQueryKey(workOrderId),
      });
      if (customerOrderId) {
        await queryClient.invalidateQueries({
          queryKey: customerOrderDetailQueryKey(customerOrderId),
        });
      }
      if (variables.action === 'delete') {
        onOpenChange(false);
      }
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(error));
      if (isWorkOrderVersionConflict(error)) {
        detailQuery.refetch();
      }
    },
  });

  const isPending = mutation.isPending;
  const actionsDisabled = isPending || detailQuery.isLoading || !detail;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交工單',
      message: '提交後將進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'submit' });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准工單',
      message: '核准後將進入生產中，可進行入庫／出庫，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'approve' });
  }

  function handleRejectConfirm(rejectReason) {
    mutation.mutate({ action: 'reject', rejectReason });
  }

  async function handleClose() {
    const ok = await confirm({
      title: '結案工單',
      message: '結案前請確認出庫／入庫條件已滿足，確定結案？',
      confirmText: '結案',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'close' });
  }

  async function handleAbort() {
    const ok = await confirm({
      title: '中止工單',
      message: '中止後為終態（僅可改備註），確定中止？',
      confirmText: '中止',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'abort' });
  }

  async function handleReopen() {
    const ok = await confirm({
      title: '重開工單',
      message: '重開後將回到生產中，確定重開？',
      confirmText: '重開',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'reopen' });
  }

  async function handleDeleteDraft() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'delete' });
  }

  function handleDialogClose() {
    if (isPending) {
      return;
    }
    onOpenChange(false);
  }

  const description = workOrderNo !== '—'
    ? (
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium">{workOrderNo}</span>
        {status ? <WorkOrderStatusBadge status={status} size="sm" /> : null}
      </div>
    )
    : undefined;

  const footer = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        disabled={isPending}
        onClick={handleDialogClose}
      >
        關閉
      </Button>
      {detailPath ? (
        <Button type="button" variant="outline" disabled={isPending} asChild>
          <Link
            to={detailPath}
            target="_blank"
            rel="noopener noreferrer"
          >
            開啟工單詳情
          </Link>
        </Button>
      ) : null}
      {canDeleteDraft ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={actionsDisabled}
          onClick={handleDeleteDraft}
        >
          刪除草稿
        </Button>
      ) : null}
      {canAbort ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={actionsDisabled}
          onClick={handleAbort}
        >
          中止
        </Button>
      ) : null}
      {canReject ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={actionsDisabled}
          onClick={() => setRejectOpen(true)}
        >
          駁回
        </Button>
      ) : null}
      {canReopen ? (
        <Button
          type="button"
          disabled={actionsDisabled}
          onClick={handleReopen}
        >
          重開
        </Button>
      ) : null}
      {canClose ? (
        <Button
          type="button"
          disabled={actionsDisabled}
          onClick={handleClose}
        >
          結案
        </Button>
      ) : null}
      {canApprove ? (
        <Button
          type="button"
          disabled={actionsDisabled}
          onClick={handleApprove}
        >
          核准
        </Button>
      ) : null}
      {canSubmit ? (
        <Button
          type="button"
          disabled={actionsDisabled}
          onClick={handleSubmit}
        >
          提交
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="退回工單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <AppDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isPending) {
            return;
          }
          onOpenChange(nextOpen);
        }}
        title="預覽工單"
        description={description}
        headerVariant="primary"
        showCloseButton
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        closeOnOutsideClick={!isPending}
        closeOnEscape={!isPending}
        footer={footer}
      >
        {!workOrderId ? (
          <p className="py-6 text-sm text-muted-foreground">缺少工單 id</p>
        ) : null}

        {workOrderId && detailQuery.isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">載入中…</p>
        ) : null}

        {workOrderId && detailQuery.isError ? (
          <p className="py-6 text-sm text-destructive">
            {getWorkOrderApiErrorMessage(detailQuery.error, '載入工單失敗')}
          </p>
        ) : null}

        {workOrderId && detail && !detailQuery.isLoading ? (
          <div
            className={cn(
              'grid grid-cols-1 gap-x-4 gap-y-3',
              'sm:grid-cols-2',
            )}
          >
            <SummaryCell label="工單號" value={workOrderNo} />
            <SummaryCell label="狀態">
              <div className="mt-0.5">
                {status
                  ? <WorkOrderStatusBadge status={status} size="sm" />
                  : (
                    <p className="text-sm font-medium text-muted-foreground">—</p>
                  )}
              </div>
            </SummaryCell>
            <SummaryCell
              label="製造標的"
              value={manufacturingTarget}
              className="col-span-1 sm:col-span-2"
            />
            <SummaryCell label="數量" value={qtyLabel} />
          </div>
        ) : null}
      </AppDialog>
    </>
  );
};

CustomerOrderWorkOrderPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workOrder: PropTypes.shape({
    workOrderId: PropTypes.string,
    workOrderNo: PropTypes.string,
    status: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  customerOrderId: PropTypes.string,
};

export default CustomerOrderWorkOrderPreviewDialog;
